import { styled } from "../../../../styles";
import { FieldHint, SectionCaption } from "../../shared";

/**
 * Styles specific to the Hospital Details tabs (EN-5172 / EN-5175). Generic
 * admin chrome lives in `shared/AdminPage.styles.ts`.
 */

/** Vertical stack of section cards inside one tab. */
export const TabStack = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
});

export const SummaryItem = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

export const SummaryLabel = styled("dt", {
  margin: 0,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "$gray-70",
  fontFamily: "$openSans",
});

export const SummaryValue = styled("dd", {
  margin: 0,
  fontSize: "14px",
  color: "$charcoal",
  fontFamily: "$openSans",
});

/** A row of per-field toggles in the ward custom-fields grid. */
export const ToggleRow = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "4px 16px",
  alignItems: "center",
});

/**
 * The onboarding-fields modal's field names and hints, a step darker than the shared
 * `$gray-60` captions so they read against the white modal (EN-5192 design review).
 */
export const OnboardingFieldCaption = styled(SectionCaption, {
  color: "$gray-70",
});

export const OnboardingFieldHint = styled(FieldHint, {
  color: "$gray-70",
});

/** Per-row outcome list in a bulk-import result. */
export const RowResultList = styled("ul", {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  maxHeight: "320px",
  overflowY: "auto",
});

export const RowResultItem = styled("li", {
  display: "flex",
  gap: "8px",
  alignItems: "baseline",
  fontSize: "13px",
  fontFamily: "$openSans",
  padding: "6px 8px",
  borderRadius: "4px",

  variants: {
    failed: {
      true: {
        backgroundColor: "$warning-lightest",
        color: "$warning-dark",
      },
      false: {
        backgroundColor: "$gray-10",
        color: "$charcoal",
      },
    },
  },
});

export const RowNumber = styled("span", {
  fontWeight: 600,
  minWidth: "44px",
  flexShrink: 0,
});

/** Counts strip on a bulk-import job. */
export const CountsRow = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px 20px",
});

/**
 * Collapsible CSV-contract block inside the import form
 * (`CsvStructureDetails`). A native `<details>` so the browser owns the
 * open/close state and the toggle stays keyboard-accessible for free.
 */
export const SpecDetails = styled("details", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",

  "&[open]": {
    paddingBottom: "4px",
  },
});

/** The toggle, styled as the link Retool showed under the file picker. */
export const SpecSummary = styled("summary", {
  cursor: "pointer",
  width: "fit-content",
  fontSize: "13px",
  fontFamily: "$openSans",
  color: "$primary",
  marginBottom: "8px",

  "&:hover": {
    textDecoration: "underline",
  },
});

/** Column-by-column contract table. */
export const SpecTable = styled("table", {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "12px",
  fontFamily: "$openSans",
  margin: "8px 0",

  "& th": {
    textAlign: "left",
    padding: "6px 8px",
    backgroundColor: "$gray-10",
    color: "$gray-70",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  "& td": {
    padding: "6px 8px",
    borderTop: "1px solid $gray-20",
    verticalAlign: "top",
    color: "$charcoal",
  },
});

/** Literal CSV value or column name. */
export const SpecCode = styled("code", {
  fontFamily: "monospace",
  fontSize: "11px",
  backgroundColor: "$gray-20",
  borderRadius: "3px",
  padding: "1px 4px",
  whiteSpace: "nowrap",
});
